/**
 * SeasonalBanner — banner decorativo en el top del main content durante
 * festividades (Navidad, Fiestas Patrias CL/PE, Año Nuevo, etc.).
 *
 * Obtiene el theme activo desde el backend: el platform admin puede
 * editarlos en /platform > Temas Estacionales.
 *
 * El usuario puede:
 *   - Dismissar un theme específico (persiste en themeStore)
 *   - Desactivar globalmente en /profile > Apariencia
 *   - Preview vía ?season=ID (guarda en sessionStorage)
 */
import { X } from 'lucide-react'
import { useThemeStore } from '@/store/themeStore'
import { useActiveSeasonalTheme } from '@/modules/platform/hooks/usePlatform'

export function SeasonalBanner() {
  const { seasonalEnabled, dismissedSeasons, dismissSeason, resolvedMode } = useThemeStore()

  // Override por URL (?season=name) para testing. Persiste en sessionStorage
  // para que sobreviva el redirect login → /dashboard.
  const urlParam = new URLSearchParams(window.location.search).get('season')
  if (urlParam) {
    try { sessionStorage.setItem('zen-season-preview', urlParam) } catch { /* ignore */ }
  }
  let previewName: string | null = null
  try { previewName = sessionStorage.getItem('zen-season-preview') } catch { /* ignore */ }

  // El mismo endpoint sirve (a) el activo natural y (b) el preview por nombre.
  const { data } = useActiveSeasonalTheme(previewName)

  if (!seasonalEnabled) return null
  const theme = data?.active ?? null
  if (!theme) return null
  if (dismissedSeasons.includes(theme.id)) return null

  const gradient = resolvedMode === 'dark' ? theme.gradient_dark : theme.gradient_light

  return (
    <div
      className="shrink-0 relative flex items-center justify-center gap-3 px-6 py-3 text-sm font-semibold"
      style={{
        background: gradient,
        color: theme.text_color,
        boxShadow: 'inset 0 -2px 4px 0 rgb(0 0 0 / 0.15)',
      }}
      role="status"
      aria-label={theme.name}
    >
      <span aria-hidden="true" className="text-xl animate-pulse-slow">{theme.emoji}</span>
      <span
        className="tracking-wide"
        style={{ textShadow: '0 1px 3px rgb(0 0 0 / 0.35)' }}
      >
        {theme.message}
      </span>
      <span aria-hidden="true" className="text-xl animate-pulse-slow">{theme.emoji}</span>
      <button
        onClick={() => dismissSeason(theme.id)}
        aria-label="Cerrar"
        title="Cerrar por ahora"
        className="absolute right-3 p-1.5 rounded-full hover:bg-black/25 transition-colors"
        style={{ color: theme.text_color }}
      >
        <X size={14} />
      </button>
    </div>
  )
}
